import React from 'react';
import clsx from 'clsx';
import useStyles from './styles';
import { useSnackbar } from 'notistack';
import ButtonBase from '@mui/material/ButtonBase';
import ContentCopy from 'mdi-material-ui/ContentCopy';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  TwitterIcon,
  TwitterShareButton,
  RedditIcon,
  RedditShareButton,
} from 'react-share';
import { ShareLinkOptions } from '../../types/socialMedia';
import { SizeVariant } from '../../types/variants';
import { useTheme } from '@mui/material/styles';

const ShareButtonGroup = (props: ShareButtonGroupPropTypes) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const {
    size,
    shareLink,
    postTitle,
    position = 'left',
    excludeEmail = true,
    excludeCopyToClipboard = true,
  } = props;

  const { enqueueSnackbar } = useSnackbar();

  const contentSize = size === 'small' ? 32 : 50;

  /**
   * Uses modern Clipboard API to copy share link to clipboard
   * https://stackoverflow.com/questions/39501189/in-reactjs-how-to-copy-text-to-clipboard
   * @Nate
   */
  const onCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      onShareFinished('Link copied to clipboard');
    } catch (err) {
      // Fallback for browsers that don't support Clipboard API
      onShareFinished('Failed to copy link');
    }
  };

  const onShareFinished = (msg: string) => {
    enqueueSnackbar(msg, {
      variant: 'info',
      preventDuplicate: true,
    });
  };

  return (
    <div
      className={clsx(
        classes.socialGroup,
        position === 'left' ? classes.left : classes.right,
      )}
    >
      <FacebookShareButton url={shareLink} className={classes.shareButton}>
        <FacebookIcon size={contentSize} />
      </FacebookShareButton>
      <div className={classes.leftSpaced}>
        <TwitterShareButton url={shareLink} className={classes.shareButton}>
          <TwitterIcon size={contentSize} />
        </TwitterShareButton>
      </div>
      <div className={classes.leftSpaced}>
        <RedditShareButton url={shareLink} className={classes.shareButton}>
          <RedditIcon
            size={contentSize}
            bgStyle={{ fill: theme.palette.brands?.reddit_orange }}
          />
        </RedditShareButton>
      </div>
      {!excludeEmail && (
        <div className={classes.leftSpaced}>
          <EmailShareButton
            subject={`Checkout this post on ELEVATOR: "${postTitle}"`}
            url={shareLink}
            className={classes.shareButton}
          >
            <EmailIcon size={contentSize} />
          </EmailShareButton>
        </div>
      )}
      {!excludeCopyToClipboard && (
        <div className={clsx(classes.leftSpaced, classes.contentCopyButtonContainer)}>
          <ButtonBase onClick={onCopyToClipboard}>
            <div
              className={clsx(
                classes.contentCopyButton,
                size === 'small' ? classes.smallButton : classes.largeButton,
              )}
            >
              <ContentCopy fontSize={'small'} />
            </div>
          </ButtonBase>
        </div>
      )}
    </div>
  );
};

export interface ShareButtonGroupPropTypes extends ShareLinkOptions {
  size: SizeVariant;
  shareLink: string;
  postTitle: string;
  position?: 'left' | 'right';
  excludeEmail?: boolean;
  excludeCopyToClipboard?: boolean;
}

export default ShareButtonGroup;
